import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../Toast';
import { askConfirm } from '../ui';
import { CalendarClock, Plus, Loader2, ChevronDown, ChevronUp, XCircle, MapPin, Video } from 'lucide-react';
import { authAxios } from '../../services/crmApi';

interface PortalMeeting {
  id: number;
  title: string;
  meeting_date: string;
  duration_minutes: number | null;
  location: string | null;
  meeting_url: string | null;
  status: string;
  notes: string | null;
}

interface Props {
  applicationId: number;
  clientName?: string;
}

export const PortalMeetingsPanel: React.FC<Props> = ({ applicationId, clientName }) => {
  const [meetings, setMeetings] = useState<PortalMeeting[]>([]);
  const [hasPortal, setHasPortal] = useState(true);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ title: '', date: '', time: '10:00', duration: 60, location: '', meeting_url: '', notes: '' });

  const load = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`/api/operator/portal-meetings/${applicationId}`);
      setMeetings(data.meetings || []);
      setHasPortal(data.has_portal_account !== false);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [applicationId]);

  useEffect(() => { if (expanded) load(); }, [expanded, load]);

  const create = async () => {
    if (!form.title.trim() || !form.date) { setMsg('Podaj tytuł i datę'); return; }
    setSaving(true);
    setMsg('');
    try {
      await authAxios.post(`/api/operator/portal-meetings/${applicationId}`, {
        title: form.title.trim(),
        meeting_date: `${form.date}T${form.time || '10:00'}:00`,
        duration_minutes: form.duration,
        location: form.location || null,
        meeting_url: form.meeting_url || null,
        notes: form.notes || null,
      });
      setForm({ title: '', date: '', time: '10:00', duration: 60, location: '', meeting_url: '', notes: '' });
      setShowForm(false);
      setMsg('Spotkanie zaplanowane — klient dostał powiadomienie w portalu');
      await load();
    } catch (e: any) {
      setMsg(e.response?.data?.error || 'Błąd zapisu');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 5000);
    }
  };

  const cancel = async (m: PortalMeeting) => {
    if (!(await askConfirm({ title: `Odwołać spotkanie "${m.title}"? Klient dostanie powiadomienie.` }))) return;
    try {
      await authAxios.patch(`/api/operator/portal-meetings/${m.id}/cancel`);
      await load();
    } catch { toast.error('Błąd odwoływania'); }
  };

  const upcoming = meetings.filter(m => m.status === 'scheduled' && new Date(m.meeting_date) > new Date());

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2">
          <CalendarClock size={18} className="text-indigo-600" />
          <span className="font-bold text-sm">
            Spotkania (portal)
            {clientName && <span className="font-normal text-gray-500 ml-1">({clientName})</span>}
          </span>
          {upcoming.length > 0 && (
            <span className="bg-indigo-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {upcoming.length}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-3 space-y-3">
          {msg && <div className="text-xs px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700">{msg}</div>}
          {!hasPortal && (
            <div className="text-xs px-3 py-2 rounded-lg bg-amber-50 text-amber-700">
              Klient nie ma konta w portalu — spotkania nie będą dla niego widoczne.
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-4"><Loader2 size={18} className="animate-spin text-gray-400" /></div>
          ) : meetings.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">Brak spotkań</p>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {meetings.map(m => (
                <div key={m.id} className={`text-xs rounded-lg border px-3 py-2 ${m.status === 'cancelled' ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-indigo-100 bg-indigo-50/40'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">{m.title}</span>
                    {m.status === 'scheduled' ? (
                      <button onClick={() => cancel(m)} title="Odwołaj" className="text-red-400 hover:text-red-600">
                        <XCircle size={13} />
                      </button>
                    ) : (
                      <span className="text-[10px] text-gray-400 uppercase">{m.status === 'cancelled' ? 'odwołane' : m.status}</span>
                    )}
                  </div>
                  <div className="text-gray-500 mt-0.5">
                    {new Date(m.meeting_date).toLocaleString('pl-PL', { dateStyle: 'medium', timeStyle: 'short' })}
                    {m.duration_minutes ? ` · ${m.duration_minutes} min` : ''}
                  </div>
                  {m.location && <div className="flex items-center gap-1 text-gray-500 mt-0.5"><MapPin size={10} /> {m.location}</div>}
                  {m.meeting_url && (
                    <a href={m.meeting_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-indigo-600 hover:underline mt-0.5">
                      <Video size={10} /> Link do spotkania
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {showForm ? (
            <div className="border border-indigo-100 rounded-lg p-3 space-y-2">
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Tytuł, np. Omówienie szczegółów wycieczki" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md" />
              <div className="grid grid-cols-3 gap-2">
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-md" />
                <select value={form.duration} onChange={e => setForm(f => ({ ...f, duration: parseInt(e.target.value) }))}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-md">
                  {[30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} min</option>)}
                </select>
              </div>
              <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder="Miejsce (np. Biuro, os. Centrum D 7)" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md" />
              <input value={form.meeting_url} onChange={e => setForm(f => ({ ...f, meeting_url: e.target.value }))}
                placeholder="Link online (Meet/Zoom) — opcjonalnie" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md" />
              <div className="flex gap-2">
                <button onClick={create} disabled={saving}
                  className="flex-1 bg-indigo-600 text-white text-xs py-1.5 rounded-md hover:bg-indigo-700 disabled:bg-gray-300">
                  {saving ? 'Zapisywanie…' : 'Zaplanuj i powiadom klienta'}
                </button>
                <button onClick={() => setShowForm(false)} className="text-xs text-gray-400 px-2">Anuluj</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowForm(true)} disabled={!hasPortal}
              className="w-full flex items-center justify-center gap-1 text-xs text-indigo-600 border border-dashed border-indigo-200 rounded-lg py-2 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed">
              <Plus size={13} /> Nowe spotkanie
            </button>
          )}
        </div>
      )}
    </div>
  );
};
